import { zodResolver } from '@hookform/resolvers/zod'
import {
	postAuthRequestPasswordReset,
	postAuthResetPassword
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { InputPassword } from '@repo/ui/commons/input-password'
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel
} from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { AuthHeader } from '@repo/ui/modules/auth/components/auth-header'
import { Spinner } from '@repo/ui/spinner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { resolveAppLanguageCode, type TKey, useTranslation } from '@/lib/i18n'
import { fetchFreshPortalSession } from '@/lib/portal-session'
import { AuthFrame } from './-components/auth-frame'

function createRequestSchema(t: (key: TKey) => string) {
	return z.object({
		email: z
			.string()
			.min(1, t('common:auth.email_required'))
			.pipe(z.email(t('common:auth.email_invalid')))
	})
}

function createResetSchema(t: (key: TKey) => string) {
	return z
		.object({
			code: z.string().min(1, t('common:auth.forgot_code_required')),
			password: z.string().min(8, t('common:auth.password_min_error')),
			confirmPassword: z
				.string()
				.min(1, t('common:auth.confirm_password_required'))
		})
		.superRefine((data, context) => {
			if (
				data.confirmPassword &&
				data.password !== data.confirmPassword
			) {
				context.addIssue({
					code: 'custom',
					message: t('common:auth.password_mismatch'),
					path: ['confirmPassword']
				})
			}
		})
}

type ResetValues = z.infer<ReturnType<typeof createResetSchema>>

export const Route = createFileRoute('/forgot-password')({
	ssr: false,
	validateSearch: z.object({ token: z.string().min(1).optional() }),
	component: ForgotPasswordPage
})

function ForgotPasswordPage() {
	const { t } = useTranslation()
	const { token } = Route.useSearch()
	const [emailOtpToken, setEmailOtpToken] = useState<string | null>(null)

	return (
		<AuthFrame>
			<AuthHeader icon={KeyRound} title={t('common:auth.forgot_title')} />
			{token || emailOtpToken ? (
				<ResetPasswordForm
					emailOtpToken={emailOtpToken}
					token={token}
				/>
			) : (
				<RequestResetForm onRequested={setEmailOtpToken} />
			)}
		</AuthFrame>
	)
}

function RequestResetForm(props: { onRequested: (token: string) => void }) {
	const { t, i18n } = useTranslation()
	const language = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const schema = createRequestSchema(t)
	const form = useForm<z.infer<ReturnType<typeof createRequestSchema>>>({
		resolver: zodResolver(schema),
		defaultValues: { email: '' }
	})
	const requestReset = useMutation({
		mutationFn: (email: string) =>
			postAuthRequestPasswordReset({
				body: { type: 'otp', email, language }
			}),
		onSuccess: (response) => {
			if ('emailOtpToken' in response.data) {
				props.onRequested(response.data.emailOtpToken)
			}
		}
	})

	return (
		<form
			onSubmit={form.handleSubmit((data) =>
				requestReset.mutate(data.email)
			)}
		>
			<FieldGroup>
				<p className='text-center text-muted-foreground text-sm'>
					{t('common:auth.forgot_description')}
				</p>
				{requestReset.isError ? (
					<AuthErrorAlert
						error={requestReset.error}
						onDismiss={requestReset.reset}
					/>
				) : null}
				<Controller
					control={form.control}
					name='email'
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor={field.name}>
								{t('common:auth.email')}
							</FieldLabel>
							<Input
								{...field}
								aria-invalid={fieldState.invalid}
								autoComplete='email'
								id={field.name}
								placeholder={t('common:auth.email_placeholder')}
								type='email'
							/>
							{fieldState.error ? (
								<FieldError errors={[fieldState.error]} />
							) : null}
						</Field>
					)}
				/>
				<Field>
					<Button disabled={requestReset.isPending} type='submit'>
						{requestReset.isPending ? (
							<Spinner />
						) : (
							t('common:auth.forgot_request_submit')
						)}
					</Button>
				</Field>
				<ButtonLink to='/login' variant='ghost'>
					{t('common:auth.back_to_login')}
				</ButtonLink>
			</FieldGroup>
		</form>
	)
}

function ResetPasswordForm(props: {
	token?: string
	emailOtpToken: string | null
}) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const schema = createResetSchema(t)
	const form = useForm<ResetValues>({
		resolver: zodResolver(schema),
		defaultValues: { code: '', password: '', confirmPassword: '' }
	})
	const resetPassword = useMutation({
		mutationFn: (data: ResetValues) =>
			props.token
				? postAuthResetPassword({
						body: {
							type: 'magic-link',
							value: props.token,
							newPassword: data.password,
							revokeOtherSessions: true
						}
					})
				: postAuthResetPassword({
						body: {
							type: 'otp',
							value: data.code,
							emailOtpToken: props.emailOtpToken ?? '',
							newPassword: data.password,
							revokeOtherSessions: true
						}
					}),
		onError: () => {
			form.resetField('code')
			form.resetField('password')
			form.resetField('confirmPassword')
		},
		onSuccess: async () => {
			await fetchFreshPortalSession(queryClient)
			await navigate({ to: '/login', search: { redirect: '/dashboard' } })
		}
	})

	return (
		<form
			onSubmit={form.handleSubmit((data) => resetPassword.mutate(data))}
		>
			<FieldGroup>
				<p className='text-center text-muted-foreground text-sm'>
					{props.token
						? t('common:auth.forgot_reset_description_link')
						: t('common:auth.forgot_reset_description_otp')}
				</p>
				{resetPassword.isError ? (
					<AuthErrorAlert
						error={resetPassword.error}
						onDismiss={resetPassword.reset}
					/>
				) : null}
				{props.token ? null : (
					<ResetField
						control={form.control}
						label={t('common:auth.forgot_code')}
						name='code'
					/>
				)}
				<ResetField
					control={form.control}
					description={t('common:auth.password_min')}
					label={t('common:auth.forgot_new_password')}
					name='password'
					type='password'
				/>
				<ResetField
					control={form.control}
					label={t('common:auth.confirm_password')}
					name='confirmPassword'
					type='password'
				/>
				<Field>
					<Button disabled={resetPassword.isPending} type='submit'>
						{resetPassword.isPending ? (
							<Spinner />
						) : (
							t('common:auth.forgot_reset_submit')
						)}
					</Button>
				</Field>
			</FieldGroup>
		</form>
	)
}

function ResetField(props: {
	control: ReturnType<typeof useForm<ResetValues>>['control']
	name: 'code' | 'password' | 'confirmPassword'
	description?: string
	label: string
	type?: string
}) {
	return (
		<Controller
			control={props.control}
			name={props.name}
			render={({ field, fieldState }) => (
				<Field data-invalid={fieldState.invalid}>
					<FieldLabel htmlFor={field.name}>{props.label}</FieldLabel>
					{props.type === 'password' ? (
						<InputPassword
							{...field}
							aria-invalid={fieldState.invalid}
							autoComplete='new-password'
							id={field.name}
						/>
					) : (
						<Input
							{...field}
							aria-invalid={fieldState.invalid}
							autoComplete='one-time-code'
							id={field.name}
							type={props.type ?? 'text'}
						/>
					)}
					{fieldState.error && (
						<FieldError errors={[fieldState.error]} />
					)}
					{!fieldState.error && props.description && (
						<FieldDescription>{props.description}</FieldDescription>
					)}
				</Field>
			)}
		/>
	)
}
